import { assertNever } from '@sparkdotfi/common-universal'
import { ExternalLinkIcon, MousePointerClickIcon } from 'lucide-react'
import { ReactNode } from 'react'

import { UseSavingsGeneralStatsResult } from '../../data/useGeneralStats'
import { Token, USD_MOCK_TOKEN } from '@/domain/types/Token'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { IconButton } from '@/ui/atoms/icon-button/IconButton'
import { ExternalLink } from '@/ui/atoms/link/Link'
import { Skeleton } from '@/ui/atoms/skeleton/Skeleton'
import { TokenIcon } from '@/ui/atoms/token-icon/TokenIcon'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/ui/atoms/tooltip/Tooltip'
import { links } from '@/ui/constants/links'
import { Info } from '@/ui/molecules/info/Info'
import { TokenAmountTooltip } from '@/ui/organisms/token-amount-tooltip/TokenAmountTooltip'
import { UsdAmountTooltip } from '@/ui/organisms/usd-amount-tooltip/UsdAmountTooltip'
import { cn } from '@/ui/utils/style'

export interface GeneralStatsBarProps {
  savingsToken: Token
  underlyingToken: Token
  generalStatsResult: UseSavingsGeneralStatsResult
  onLiquiditySparkVaultsV2MoreInfoClick?: () => void
  isLiquiditySparkVaultsV2Interactive?: boolean
}

export function GeneralStatsBar({
  savingsToken,
  underlyingToken,
  generalStatsResult,
  onLiquiditySparkVaultsV2MoreInfoClick,
  isLiquiditySparkVaultsV2Interactive,
}: GeneralStatsBarProps) {
  const { queryResult, statsItems } = generalStatsResult

  if (statsItems.length === 0) {
    return null
  }

  if (queryResult.isError) {
    return null
  }

  if (queryResult.isPending) {
    return <Skeleton className="h-10 w-full max-w-[400px]" />
  }

  const link = getMoreLink(savingsToken)

  return (
    <div className={cn('inline-flex items-stretch rounded-[10px]', 'bg-primary/80 py-3 backdrop-blur-lg')}>
      <div
        className={cn(
          statsItems.length > 3 ? 'grid grid-cols-[auto_auto] gap-y-1' : 'inline-flex',
          'sm:-ml-1 ml-1 items-center divide-x divide-secondary md:inline-flex',
        )}
      >
        {statsItems.map((item) => {
          switch (item) {
            case 'tvl':
              return <TvlStat {...queryResult.data} key={item} underlyingToken={underlyingToken} />
            case 'users':
              return <UsersStat {...queryResult.data} key={item} />
            case 'liquidity-sky-psm':
              return <LiquidityStat {...queryResult.data} key={item} type="sky" />
            case 'liquidity-spark-psm':
              return <LiquidityStat {...queryResult.data} key={item} type="spark" />
            case 'deposit-cap':
              return <DepositCapStat {...queryResult.data} key={item} underlyingToken={underlyingToken} />
            case 'liquidity-stusds':
              return <LiquidityStusdsStat {...queryResult.data} key={item} />
            case 'liquidity-spark-vaults-v2':
              return (
                <LiquiditySparkVaultsV2Stat
                  {...queryResult.data}
                  key={item}
                  underlyingToken={underlyingToken}
                  onMoreInfoClick={onLiquiditySparkVaultsV2MoreInfoClick}
                  isInteractive={isLiquiditySparkVaultsV2Interactive}
                />
              )
            default:
              assertNever(item)
          }
        })}
      </div>
      {import.meta.env.VITE_BASE_MINI_APP !== '1' && (
        <Stat className="pr-3 sm:pr-3">
          <ExternalLink to={link} variant="decorator" className="flex">
            <IconButton variant="transparent" size="s" icon={ExternalLinkIcon} />
          </ExternalLink>
        </Stat>
      )}
    </div>
  )
}

function getMoreLink(savingsToken: Token) {
  return savingsToken.symbol === TokenSymbol('stUSDS')
    ? 'https://info.sky.money/stUSDS/'
    : links.sparkExternal.dataDashboardSavings
}

type GeneralStatsData = NonNullable<UseSavingsGeneralStatsResult['queryResult']['data']>

function TvlStat({ tvl, underlyingToken }: GeneralStatsData & { underlyingToken: Token }) {
  return (
    <Stat>
      <Label>TVL:</Label>
      <Value>
        {underlyingToken.symbol !== TokenSymbol('ETH') ? (
          <UsdAmountTooltip token={underlyingToken} amount={tvl} style="compact" />
        ) : (
          <>
            <TokenAmountTooltip
              token={underlyingToken}
              amount={tvl}
              options={{ style: 'compact', showSymbol: 'always' }}
            />
            <span className="text-secondary"> / </span>
            <UsdAmountTooltip token={underlyingToken} amount={tvl} style="compact" />
          </>
        )}
      </Value>
    </Stat>
  )
}

function UsersStat({ users }: GeneralStatsData) {
  return (
    <Stat>
      <Label>Users:</Label>
      <Value>{formatUsersNumber(users)}</Value>
    </Stat>
  )
}

function DepositCapStat({ depositCap, underlyingToken }: GeneralStatsData & { underlyingToken: Token }) {
  if (!depositCap) {
    return null
  }

  return (
    <Stat>
      <Label>Deposit cap:</Label>
      <Value className="flex items-center gap-0.5">
        <TokenIcon className="size-3 md:size-4" token={underlyingToken} />
        <TokenAmountTooltip
          token={underlyingToken}
          amount={depositCap}
          options={{ style: 'compact', showSymbol: 'only-tooltip-content' }}
        />
      </Value>
    </Stat>
  )
}

function LiquidityStat({ liquidity, type }: GeneralStatsData & { type: 'sky' | 'spark' }) {
  if (!liquidity) {
    return null
  }

  return (
    <Stat>
      <Label>Liquidity:</Label>
      <Value className="flex items-center gap-1">
        <UsdAmountTooltip token={USD_MOCK_TOKEN} amount={liquidity} style="compact" />
        {type === 'sky' ? <SkyPsmLiquidityInfo /> : <SparkPsmLiquidityInfo />}
      </Value>
    </Stat>
  )
}

function LiquidityStusdsStat({ liquidity }: GeneralStatsData) {
  if (!liquidity) {
    return null
  }

  return (
    <Stat>
      <Label>Liquidity:</Label>
      <Value className="flex items-center gap-1">
        <UsdAmountTooltip token={USD_MOCK_TOKEN} amount={liquidity} style="compact" />
        <Info>
          Worth of USDS currently idle in the stUSDS pool and available for immediate withdrawal or new borrowing.
          Higher liquidity generally means smoother and more efficient transactions. <br />
          {import.meta.env.VITE_BASE_MINI_APP !== '1' && (
            <ExternalLink to={links.docs.savings.stusds}>Learn more</ExternalLink>
          )}
        </Info>
      </Value>
    </Stat>
  )
}

function LiquiditySparkVaultsV2Stat({
  liquidity,
  systemAvailableLiquidity,
  underlyingToken,
  onMoreInfoClick,
  isInteractive,
}: GeneralStatsData & {
  underlyingToken: Token
  onMoreInfoClick?: () => void
  isInteractive?: boolean
}) {
  if (!liquidity) {
    return null
  }

  const formattedLiquidity = underlyingToken.format(systemAvailableLiquidity ?? liquidity, { style: 'compact' })
  const canOpenMoreInfo = isInteractive && onMoreInfoClick !== undefined

  if (canOpenMoreInfo) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={onMoreInfoClick}
            className={cn(
              'group flex cursor-pointer items-center gap-[3px] rounded-sm px-2 text-left transition-colors sm:gap-1 sm:px-4',
              'focus-visible:outline-hidden focus-visible:ring-3 focus-visible:ring-primary-200',
            )}
          >
            <MousePointerClickIcon className="size-4 text-secondary group-hover:text-primary" />
            <div className="typography-label-3 text-secondary group-hover:text-primary">Liquidity:</div>
            <Value className="flex items-center gap-0.5">
              <TokenIcon className="size-3 md:size-4" token={underlyingToken} />
              <span>{formattedLiquidity}</span>
              <span className="text-secondary group-hover:text-primary">{underlyingToken.symbol}</span>
            </Value>
          </button>
        </TooltipTrigger>
        <TooltipContent>Click to get more info</TooltipContent>
      </Tooltip>
    )
  }

  return (
    <Stat>
      <Label>Liquidity:</Label>
      <Value className="flex items-center gap-0.5">
        <TokenIcon className="size-3 md:size-4" token={underlyingToken} />
        <TokenAmountTooltip
          token={underlyingToken}
          amount={systemAvailableLiquidity ?? liquidity}
          options={{ style: 'compact', showSymbol: 'only-tooltip-content' }}
        />
        {systemAvailableLiquidity ? (
          <Info>
            Immediate liquidity available for withdrawals is{' '}
            <span className="text-primary-inverse">
              {underlyingToken.format(liquidity, { style: 'compact' })} {underlyingToken.symbol}
            </span>
            .<br />
            The Spark Liquidity Layer will refill liquidity in the next block if needed.{' '}
            <span className="whitespace-nowrap">
              <ExternalLink to={links.docs.sparkLiquidityLayer}>Learn more</ExternalLink>
            </span>
          </Info>
        ) : (
          <Info>
            Spark Liquidity Layer maintains the target liquidity for each vault. More capital will move in automatically
            when you withdraw a large amount.{' '}
            {import.meta.env.VITE_BASE_MINI_APP !== '1' && (
              <span className="whitespace-nowrap">
                <ExternalLink to={links.docs.sparkLiquidityLayer}>Learn more</ExternalLink>
              </span>
            )}
          </Info>
        )}
      </Value>
    </Stat>
  )
}

function Stat({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('flex items-center gap-[3px] px-2 sm:gap-1 sm:px-4', className)}>{children}</div>
}

function Label({ children }: { children: string }) {
  return <div className="typography-label-3 text-secondary">{children}</div>
}

function Value({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('typography-label-3 text-primary', className)}>{children}</div>
}

function formatUsersNumber(users: number): string {
  return Intl.NumberFormat('en-US', { maximumFractionDigits: 0, notation: 'compact' }).format(users)
}

function SkyPsmLiquidityInfo() {
  return (
    <Info>
      The liquidity is provided by the Sky Peg Stability Module which allows 1:1 swaps between USDC and USDS without
      slippage or fees beyond network fees.{' '}
      {import.meta.env.VITE_BASE_MINI_APP !== '1' && (
        <ExternalLink to={links.docs.savings.withdrawSavings}>Learn more</ExternalLink>
      )}
    </Info>
  )
}

function SparkPsmLiquidityInfo() {
  return (
    <Info>
      The liquidity is provided by the Spark Peg Stability Module which allows 1:1 swaps between USDC and USDS without
      slippage or fees beyond network fees.{' '}
      {import.meta.env.VITE_BASE_MINI_APP !== '1' && (
        <ExternalLink to={links.docs.savings.sparkPSM}>Learn more</ExternalLink>
      )}
    </Info>
  )
}
